"use client";

import { ErrorMessage } from "@hookform/error-message";
import { Label } from "radix-ui";
import { type ComponentProps, type ReactNode, forwardRef, useId } from "react";
import { type FieldValues, type RegisterOptions, useFormContext } from "react-hook-form";
import { FormErrorMessage, Typography, cn } from "../../..";

interface SelectProps extends Omit<ComponentProps<"select">, "size"> {
	label?: string;
	description?: ReactNode;
	size?: "sm" | "default";
	registerOptions?: RegisterOptions<FieldValues>;
	innerClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function SelectRef(props, ref) {
	const {
		label,
		size = "default",
		registerOptions,
		description,
		className,
		innerClassName,
		...rest
	} = props;

	const methods = useFormContext();

	const registeredProps =
		methods && props.name ? methods.register(props.name, registerOptions) : { ref };

	const id = useId();
	const errorId = `${id}-error`;
	const descriptionId = `${id}-description`;

	const hasError = props.name ? methods?.getFieldState(props.name)?.invalid : false;

	return (
		<div className={cn(className, "flex flex-col gap-1")}>
			{!!label && (
				<Label.Root htmlFor={id} className="self-start font-bold">
					{label}
				</Label.Root>
			)}

			<select
				id={id}
				{...rest}
				{...registeredProps}
				className={cn(
					{
						"border-danger ring-danger focus-visible:border-danger focus-visible:ring-danger":
							hasError,
						"border-shades hover:border-primary-medium focus-visible:border-primary-medium focus-visible:ring-primary-medium":
							!hasError,
						"py-1 text-sm": size === "sm",
						"selectArrow form-select": !props.multiple,
						"form-multiselect scrollbar-none": props.multiple,
					},
					"w-full max-w-full overflow-x-hidden rounded border bg-white focus:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:bg-shades-light/70 disabled:opacity-80 motion-safe:transition-colors",
					innerClassName,
				)}
				aria-describedby={[description ? descriptionId : false, hasError ? errorId : false]
					.filter(Boolean)
					.join(" ")}
			>
				{props.children}
			</select>

			{!!description && (
				<Typography
					id={descriptionId}
					className="mt-1 text-shades-dark text-sm"
					variant="paragraph"
				>
					{props.description}
				</Typography>
			)}

			{hasError && !!props.name && (
				<FormErrorMessage variant="danger" id={errorId}>
					<ErrorMessage name={props.name} />
				</FormErrorMessage>
			)}
		</div>
	);
});
